import { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { api, ApiError } from '../api';
import { Avatar } from '../components/Avatar';
import { IconImage, IconLink, IconTrash, IconChat } from '../components/Icon';
import { UiNode } from '../lib/UiNode';
import { CardDetail } from '../components/CardDetail';
import type { BoardDetail, IdeaAttachment, IdeaWithReactions, User } from '../../shared/types';

/**
 * Capture — the meat of SynFolio.
 *
 * Mobile-first. One big composer at the top; ideas stream in below.
 * The whole page is a drop target: drag a screenshot, a URL, or any text
 * onto it and it becomes a new idea. Pasting works the same.
 */
export function CapturePhase({
  board,
  ideas,
  user,
  onIdeasChange,
}: {
  board: BoardDetail;
  ideas: IdeaWithReactions[];
  user: User;
  onIdeasChange: (cb: (cur: IdeaWithReactions[]) => IdeaWithReactions[]) => void;
}) {
  const [composer, setComposer] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const upsertIdea = useCallback(
    (next: IdeaWithReactions) => {
      onIdeasChange((cur) => {
        const existing = cur.find((i) => i.id === next.id);
        return existing ? cur.map((i) => (i.id === next.id ? next : i)) : [...cur, next];
      });
    },
    [onIdeasChange],
  );

  /** Create an idea with an optional immediate attachment payload. */
  const create = useCallback(
    async (opts: { text: string; file?: File | null; url?: string | null }) => {
      const text = (opts.text ?? '').trim();
      const url = (opts.url ?? '').trim();
      const finalText = text || (url ? truncateUrl(url) : opts.file ? 'Image' : '');
      if (!finalText) return;
      setBusy(true);
      try {
        const r = await api.createIdea(board.id, { text: finalText });
        const placeholder: IdeaWithReactions = {
          id: r.id,
          boardId: board.id,
          authorId: user.id,
          text: finalText,
          note: null,
          emoji: r.emoji,
          colorHue: r.colorHue,
          clusterId: null,
          createdAt: Math.floor(Date.now() / 1000),
          updatedAt: Math.floor(Date.now() / 1000),
          attachments: [],
          reactions: { love: 0, curious: 0, pass: 0 },
          myReaction: null,
        };
        upsertIdea(placeholder);

        if (url) {
          try {
            const a = await api.addLink(board.id, r.id, url);
            upsertIdea({ ...placeholder, attachments: [a.attachment] });
          } catch {
            // Best-effort; the idea still exists.
          }
        } else if (opts.file) {
          try {
            const up = await api.uploadImage(board.id, r.id, opts.file);
            const att: IdeaAttachment = {
              id: up.id,
              kind: 'image',
              url: null,
              title: null,
              description: null,
              siteName: null,
              previewImageUrl: null,
              imageUrl: `/api/media/${encodeURIComponent(up.r2Key)}`,
              width: null,
              height: null,
              byteSize: opts.file.size,
              mimeType: opts.file.type,
              orderIndex: 0,
            };
            upsertIdea({ ...placeholder, attachments: [att] });
          } catch {
            /* ignored */
          }
        }
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : 'Something went wrong.';
        console.error(msg);
      } finally {
        setBusy(false);
        setComposer('');
        composerRef.current?.focus();
      }
    },
    [board.id, upsertIdea, user.id],
  );

  /** Look for URLs or images in pasted/dropped data. */
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    for (const it of items) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          void create({ text: composer, file });
          return;
        }
      }
    }
    // If the textarea is empty and clipboard contains exactly a URL, attach it.
    const text = e.clipboardData.getData('text/plain').trim();
    if (!composer.trim() && isUrl(text)) {
      e.preventDefault();
      void create({ text: '', url: text });
    }
  };

  return (
    <div
      className="flex flex-col gap-4"
      onDragOver={(e) => {
        if (Array.from(e.dataTransfer.types).some((t) => t === 'Files' || t === 'text/plain')) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={async (e) => {
        e.preventDefault();
        setDragOver(false);
        const stash = composer;
        const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith('image/'));
        if (files.length > 0) {
          // First file takes the composer text as its caption, others land plain.
          let first = true;
          for (const f of files) {
            await create({ text: first ? stash : '', file: f });
            first = false;
          }
          return;
        }
        const text = e.dataTransfer.getData('text/plain').trim();
        if (isUrl(text)) await create({ text: stash, url: text });
        else if (text) await create({ text: stash ? `${stash}\n${text}` : text });
      }}
    >
      <Composer
        composerRef={composerRef}
        value={composer}
        onChange={setComposer}
        onSubmit={() => void create({ text: composer })}
        onPaste={onPaste}
        busy={busy}
      />

      {dragOver && (
        <div className="rounded-2xl border-2 border-dashed border-clove/60 bg-clove/5 px-4 py-8 text-center text-sm text-cream-2">
          Drop to add as an idea.
        </div>
      )}

      <ul className="flex flex-col gap-3">
        <AnimatePresence initial={false}>
          {[...ideas]
            .sort((a, b) => b.createdAt - a.createdAt)
            .map((i) => (
              <motion.li
                key={i.id}
                layout
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.18 }}
              >
                <IdeaCard
                  idea={i}
                  meId={user.id}
                  authorName={authorName(board, i.authorId, user)}
                  authorHue={authorHue(board, i.authorId, user)}
                  board={board}
                  onDelete={async () => {
                    await api.deleteIdea(board.id, i.id);
                    onIdeasChange((cur) => cur.filter((x) => x.id !== i.id));
                  }}
                  onAddLink={async (url) => {
                    const a = await api.addLink(board.id, i.id, url);
                    onIdeasChange((cur) =>
                      cur.map((x) =>
                        x.id === i.id ? { ...x, attachments: [...x.attachments, a.attachment] } : x,
                      ),
                    );
                  }}
                  onAddImage={async (file) => {
                    const up = await api.uploadImage(board.id, i.id, file);
                    const att: IdeaAttachment = {
                      id: up.id,
                      kind: 'image',
                      url: null,
                      title: null,
                      description: null,
                      siteName: null,
                      previewImageUrl: null,
                      imageUrl: `/api/media/${encodeURIComponent(up.r2Key)}`,
                      width: null,
                      height: null,
                      byteSize: file.size,
                      mimeType: file.type,
                      orderIndex: i.attachments.length,
                    };
                    onIdeasChange((cur) =>
                      cur.map((x) =>
                        x.id === i.id ? { ...x, attachments: [...x.attachments, att] } : x,
                      ),
                    );
                  }}
                />
              </motion.li>
            ))}
        </AnimatePresence>
      </ul>

      {ideas.length === 0 && <CaptureEmpty />}
    </div>
  );
}

function Composer({
  composerRef,
  value,
  onChange,
  onSubmit,
  onPaste,
  busy,
}: {
  composerRef: React.RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onPaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  busy: boolean;
}) {
  return (
    <UiNode name="capture-composer" entity="idea" className="panel-elev p-3 sm:p-4">
      <UiNode
        as="textarea"
        name="capture-composer-textarea"
        entity="idea"
        action="compose"
        state={busy ? 'busy' : 'idle'}
        ref={composerRef}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          onChange(e.target.value);
          // Auto-grow vertically to fit content (capped at 8 rows).
          const el = e.currentTarget;
          el.style.height = 'auto';
          el.style.height = Math.min(el.scrollHeight, 8 * 24) + 'px';
        }}
        onPaste={onPaste}
        onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSubmit();
          } else if (e.key === 'Enter' && !e.shiftKey && !value.includes('\n')) {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder="Drop an idea or paste a link."
        rows={1}
        className="w-full resize-none bg-transparent text-[16px] outline-none placeholder:text-cream-3"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="hidden text-[11px] text-cream-3 sm:block">
          Enter to add. Drop or paste anywhere.
        </p>
        <p className="text-[11px] text-cream-3 sm:hidden">Tap Add to save.</p>
        <UiNode
          as="button"
          name="capture-composer-submit-button"
          entity="idea"
          action="create"
          state={busy ? 'busy' : 'idle'}
          className="btn btn-accent"
          disabled={!value.trim() || busy}
          onClick={onSubmit}
        >
          {busy ? 'Adding…' : 'Add'}
        </UiNode>
      </div>
    </UiNode>
  );
}

function CaptureEmpty() {
  return (
    <UiNode
      name="capture-empty-state"
      entity="idea"
      state="empty"
      className="panel flex flex-col items-center gap-2 px-6 py-12 text-center"
    >
      <p className="text-base">Nothing in yet.</p>
      <p className="max-w-xs text-sm text-cream-3">
        One line is enough. When you have a few, move on.
      </p>
    </UiNode>
  );
}

function IdeaCard({
  idea,
  meId,
  authorName,
  authorHue,
  board,
  onDelete,
  onAddLink,
  onAddImage,
}: {
  idea: IdeaWithReactions;
  meId: string;
  authorName: string;
  authorHue: number;
  board: BoardDetail;
  onDelete: () => void;
  onAddLink: (url: string) => Promise<void>;
  onAddImage: (file: File) => Promise<void>;
}) {
  const mine = idea.authorId === meId;
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <UiNode
      as="article"
      name="capture-idea-card"
      entity="idea"
      entityId={idea.id}
      className="panel group p-4"
      style={
        idea.colorHue != null
          ? {
              borderLeft: `3px solid hsl(${idea.colorHue}, 70%, 55%)`,
              background: `linear-gradient(135deg, hsla(${idea.colorHue}, 50%, 18%, 0.35) 0%, transparent 60%), var(--color-ink-2)`,
            }
          : undefined
      }
    >
      <div className="flex items-start gap-3">
        <Avatar
          user={{ id: idea.authorId, name: authorName, avatarHue: authorHue, avatarUrl: null }}
          size={32}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            {idea.emoji && (
              <span aria-hidden className="text-base leading-none">{idea.emoji}</span>
            )}
            <span className="text-sm font-medium">{authorName}</span>
            <span className="text-[11px] text-cream-3">{relativeTime(idea.createdAt)}</span>
          </div>
          <p className="mt-1 whitespace-pre-wrap break-words text-[15px] leading-snug">
            {idea.text}
          </p>

          {idea.attachments.length > 0 && (
            <div className="mt-3 flex flex-col gap-2">
              {idea.attachments
                .filter((a) => a.kind === 'link')
                .map((a) => (
                  <LinkCard key={a.id} attachment={a} />
                ))}
              {idea.attachments.some((a) => a.kind === 'image') && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {idea.attachments
                    .filter((a) => a.kind === 'image')
                    .map((a) => (
                      <a
                        key={a.id}
                        href={a.imageUrl ?? '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block overflow-hidden rounded-xl border border-ink-rim"
                      >
                        {a.imageUrl && (
                          <img
                            src={a.imageUrl}
                            alt=""
                            className="aspect-square w-full object-cover"
                            loading="lazy"
                          />
                        )}
                      </a>
                    ))}
                </div>
              )}
            </div>
          )}

          {linkOpen && (
            <form
              className="mt-3 flex gap-2"
              onSubmit={async (e) => {
                e.preventDefault();
                const u = linkValue.trim();
                if (!isUrl(u)) return;
                await onAddLink(u);
                setLinkValue('');
                setLinkOpen(false);
              }}
            >
              <input
                autoFocus
                className="input"
                type="url"
                placeholder="https://"
                value={linkValue}
                onChange={(e) => setLinkValue(e.target.value)}
              />
              <button type="submit" className="btn btn-primary" disabled={!isUrl(linkValue.trim())}>
                Add
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setLinkOpen(false);
                  setLinkValue('');
                }}
              >
                Cancel
              </button>
            </form>
          )}
        </div>

        <div className="flex items-center gap-1 self-start">
          <IconButton
            name="capture-idea-discuss-button"
            entityId={idea.id}
            label="Open discussion"
            onClick={() => setDetailOpen(true)}
          >
            <IconChat size={20} />
          </IconButton>
          <IconButton
            name="capture-idea-add-image-button"
            entityId={idea.id}
            label="Add image"
            onClick={() => fileInputRef.current?.click()}
          >
            <IconImage size={20} />
          </IconButton>
          <IconButton
            name="capture-idea-add-link-button"
            entityId={idea.id}
            label="Add link"
            onClick={() => setLinkOpen((v) => !v)}
          >
            <IconLink size={20} />
          </IconButton>
          {mine && (
            <IconButton
              name="capture-idea-delete-button"
              entityId={idea.id}
              label="Delete idea"
              onClick={() => {
                if (window.confirm('Delete this idea?')) onDelete();
              }}
            >
              <IconTrash size={20} />
            </IconButton>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void onAddImage(f);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <CardDetail
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        board={board}
        kind="idea"
        subjectId={idea.id}
        header={
          <span className="inline-flex items-center gap-2">
            {idea.emoji && <span>{idea.emoji}</span>}
            <span className="truncate">{idea.text}</span>
          </span>
        }
        body={
          idea.note ? (
            <p className="rounded-md bg-ink-3 p-2 text-cream-2">{idea.note}</p>
          ) : null
        }
      />
    </UiNode>
  );
}

function IconButton({
  name,
  entityId,
  label,
  onClick,
  children,
}: {
  name:
    | 'capture-idea-add-image-button'
    | 'capture-idea-add-link-button'
    | 'capture-idea-delete-button'
    | 'capture-idea-discuss-button';
  entityId: string;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const action =
    name === 'capture-idea-add-image-button'
      ? 'attach_image'
      : name === 'capture-idea-add-link-button'
        ? 'attach_link'
        : name === 'capture-idea-discuss-button'
          ? 'discuss'
          : 'delete';
  return (
    <UiNode
      as="button"
      name={name}
      entity="idea"
      entityId={entityId}
      action={action}
      type="button"
      aria-label={label}
      title={label}
      className="flex h-9 w-9 items-center justify-center rounded-md text-cream-3 transition hover:bg-ink-3 hover:text-cream"
      onClick={onClick}
    >
      {children}
    </UiNode>
  );
}

function LinkCard({ attachment }: { attachment: IdeaAttachment }) {
  if (!attachment.url) return null;
  const host = (() => {
    try {
      return new URL(attachment.url).hostname.replace(/^www\./, '');
    } catch {
      return attachment.url;
    }
  })();
  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noopener noreferrer"
      className="panel-elev flex items-center gap-3 overflow-hidden p-3 transition hover:border-cream-3"
    >
      {attachment.previewImageUrl ? (
        <img
          src={attachment.previewImageUrl}
          alt=""
          className="h-14 w-14 rounded-md object-cover"
          loading="lazy"
        />
      ) : (
        <div className="flex h-14 w-14 items-center justify-center rounded-md bg-ink-4 text-cream-3">
          🔗
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">
          {attachment.title ?? host}
        </div>
        {attachment.description && (
          <div className="truncate text-xs text-cream-3">{attachment.description}</div>
        )}
        <div className="truncate text-[11px] text-cream-4">{host}</div>
      </div>
    </a>
  );
}

// ---- Helpers -------------------------------------------------------------

function isUrl(s: string): boolean {
  if (!s) return false;
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function truncateUrl(u: string): string {
  try {
    const url = new URL(u);
    return url.hostname.replace(/^www\./, '') + (url.pathname === '/' ? '' : url.pathname);
  } catch {
    return u;
  }
}

function relativeTime(epochSec: number): string {
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - epochSec);
  if (diff < 30) return 'just now';
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86_400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86_400)}d`;
}

function authorName(board: BoardDetail, authorId: string, me: User): string {
  if (authorId === me.id) return me.name;
  const m = board.members.find((mm) => mm.id === authorId);
  return m?.name ?? 'Someone';
}
function authorHue(board: BoardDetail, authorId: string, me: User): number {
  if (authorId === me.id) return me.avatarHue;
  const m = board.members.find((mm) => mm.id === authorId);
  return m?.avatarHue ?? 200;
}
